import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface AdminOrgMembership {
  organization_id: number;
  organization_name: string;
  organization_slug: string;
  role: string;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  created_at: string;
  system_roles: string[];
  organizations: AdminOrgMembership[];
}

export interface AdminOrg {
  id: number;
  name: string;
  slug: string;
  part_number_prefix: string;
  prefer_detailed_description: boolean;
  member_count: number;
  part_count: number;
  bom_count: number;
  created_at: string;
}

export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<AdminUser[]>('/api/admin/users'),
  });
}

export function useAdminOrgs(opts?: { enabled?: boolean }) {
  return useQuery<AdminOrg[]>({
    queryKey: ['admin', 'orgs'],
    queryFn: () => api.get<AdminOrg[]>('/api/admin/orgs'),
    enabled: opts?.enabled ?? true,
  });
}

export function useToggleUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, active }: { userId: number; active: boolean }) =>
      api.postJson<AdminUser>(
        `/api/admin/users/${userId}/${active ? 'enable' : 'disable'}`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export interface AdminCreateUserInput {
  email: string;
  password?: string;
  name?: string;
  is_verified?: boolean;
  is_superuser?: boolean;
  grant_admin_role?: boolean;
  organization_id?: number | null;
  organization_role?: string;
}

export interface AdminCreateUserResponse {
  user: AdminUser;
  generated_password: string | null;
}

export function useAdminCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminCreateUserInput) =>
      api.postJson<AdminCreateUserResponse>('/api/admin/users', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });
}

export interface AdminCreateOrgInput {
  name: string;
  slug?: string;
  part_number_prefix?: string;
  owner_user_id?: number | null;
}

export function useAdminCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminCreateOrgInput) =>
      api.postJson<{ id: number; slug: string; name: string }>(
        '/api/admin/orgs',
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ---- Admin edit endpoints ------------------------------------------------

export interface AdminUserUpdateInput {
  email?: string;
  name?: string | null;
  is_superuser?: boolean;
  is_active?: boolean;
  is_verified?: boolean;
}

export function useAdminUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: number; input: AdminUserUpdateInput }) =>
      api.patchJson<AdminUser>(`/api/admin/users/${userId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useAdminGrantSystemRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleName }: { userId: number; roleName: string }) =>
      api.postJson<unknown>(
        `/api/admin/users/${userId}/system-roles/${roleName}`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useAdminRevokeSystemRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleName }: { userId: number; roleName: string }) =>
      api.delete<unknown>(
        `/api/admin/users/${userId}/system-roles/${roleName}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export interface AddMembershipInput {
  organization_id: number;
  role: string;
}

export function useAdminAddMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: number; input: AddMembershipInput }) =>
      api.postJson<AdminOrgMembership>(
        `/api/admin/users/${userId}/org-memberships`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });
}

export function useAdminUpdateMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      role,
    }: {
      userId: number;
      orgId: number;
      role: string;
    }) =>
      api.patchJson<AdminOrgMembership>(
        `/api/admin/users/${userId}/org-memberships/${orgId}`,
        { role },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useAdminRemoveMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, orgId }: { userId: number; orgId: number }) =>
      api.delete<unknown>(
        `/api/admin/users/${userId}/org-memberships/${orgId}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export interface AdminOrgUpdateInput {
  name?: string;
  slug?: string;
  part_number_prefix?: string;
  prefer_detailed_description?: boolean;
}

export function useAdminUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgSlug, input }: { orgSlug: string; input: AdminOrgUpdateInput }) =>
      api.patchJson<{
        id: number;
        slug: string;
        name: string;
        part_number_prefix: string;
        prefer_detailed_description: boolean;
      }>(`/api/admin/orgs/${orgSlug}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useAdminDeleteOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgSlug: string) =>
      api.delete<unknown>(`/api/admin/orgs/${orgSlug}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
